'use client';

import { useMemo, type MouseEvent, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { formatDistanceToNow } from 'date-fns';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  FileCodeIcon,
  GithubIcon,
  GitlabIcon,
  InformationCircleIcon,
  MoreVerticalIcon,
  SourceCodeIcon,
} from '@terra/ui-icons';
import { Alert } from '../../../components/alert';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import { Skeleton } from '../../../components/skeleton';
import {
  MenuWithItems,
  type MenuItemProps,
} from '../../../components/styled/menu';
import { Tooltip } from '../../../components/tooltip';
import { BodyStrong, MetaDefault } from '../../../components/typography';
import { TableGrid } from '../../table-grid';
import {
  isInternalDeploymentIntegration,
  isZipUploadIntegration,
  resolvePermissionTypeKey,
  resolveSourceCodeInfoKind,
  resolveSourceCodeIntegrationName,
  resolveSourceCodeMenuActions,
  resolveSourceCodeProvider,
  resolveWebhookSetupLabel,
  SOURCE_CODE_INFO_KINDS,
  SOURCE_CODE_MENU_ACTIONS,
  SOURCE_CODE_PROVIDERS,
  type SourceCodeIntegrationDetails,
  type SourceCodeMenuAction,
  type SourceCodeProvider,
  type SourceCodeRepositoryRow,
} from './source-code-integration.model';

export type SourceCodeIntegrationCardProps = {
  integration: SourceCodeIntegrationDetails;
  isMounted?: boolean;
  connectedAt?: Date | string | null;
  repositories: SourceCodeRepositoryRow[];
  pollingIntervalHours?: number | null;
  isValidIntegration?: boolean | null;
  isLoading?: boolean;
  isReadOnly?: boolean;
  providerLogo?: ReactNode;
  onEdit?: () => void;
  onAddRepositories?: () => void;
  onTransform?: () => void;
  onDisconnect?: () => void;
};

const MENU_LABEL_KEYS = {
  [SOURCE_CODE_MENU_ACTIONS.EDIT_NAME]: 'editIntegrationName',
  [SOURCE_CODE_MENU_ACTIONS.EDIT_REPOSITORIES]: 'editRepositories',
  [SOURCE_CODE_MENU_ACTIONS.TRANSFORM]: 'transformZipIntegration',
  [SOURCE_CODE_MENU_ACTIONS.DISCONNECT]: 'disconnectIntegration',
} as const satisfies Record<SourceCodeMenuAction, string>;

const PROVIDER_ICONS = {
  [SOURCE_CODE_PROVIDERS.GITHUB]: GithubIcon,
  [SOURCE_CODE_PROVIDERS.GITLAB]: GitlabIcon,
  [SOURCE_CODE_PROVIDERS.AZURE_DEVOPS]: SourceCodeIcon,
} as const;

function DefaultProviderLogo({
  provider,
  isZipUpload,
}: {
  provider: SourceCodeProvider | null;
  isZipUpload: boolean;
}) {
  const Icon =
    isZipUpload || !provider ? FileCodeIcon : PROVIDER_ICONS[provider];

  return (
    <Box
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="32px"
      h="32px"
      borderRadius="full"
      bg="bg.neutral.subtle"
    >
      <Icon size={18} color={token('colors.fg.muted')} />
    </Box>
  );
}

export function SourceCodeIntegrationCard({
  integration,
  isMounted = false,
  connectedAt,
  repositories,
  pollingIntervalHours,
  isValidIntegration,
  isLoading,
  isReadOnly,
  providerLogo,
  onEdit,
  onAddRepositories,
  onTransform,
  onDisconnect,
}: SourceCodeIntegrationCardProps) {
  const t = useTranslations('designSystem.applicationSettings.sourceCode');
  const provider = resolveSourceCodeProvider(integration.platform);
  const isZipUpload = isZipUploadIntegration(integration.platform);
  const infoKind = resolveSourceCodeInfoKind({ integration, isMounted });
  const integrationName = resolveSourceCodeIntegrationName({
    integration,
    isMounted,
    titles: {
      github: t('providers.github'),
      gitlab: t('providers.gitlab'),
      azureDevOps: t('providers.azureDevOps'),
      mounted: t('providers.mounted'),
    },
  });
  const connectedLabel = connectedAt
    ? t('connectedAt', {
        date: formatDistanceToNow(new Date(connectedAt), { addSuffix: true }),
      })
    : '';
  const infoParts = useMemo(() => {
    if (infoKind === SOURCE_CODE_INFO_KINDS.MOUNTED) {
      return [t('mountedInfoLabel'), connectedLabel];
    }

    if (infoKind === SOURCE_CODE_INFO_KINDS.ZIP) {
      return [
        repositories[0]?.name ?? '',
        t('zipFile'),
        t('oneTimeAccess'),
        connectedLabel,
      ];
    }

    if (infoKind === SOURCE_CODE_INFO_KINDS.APP) {
      const permissionKey = resolvePermissionTypeKey(integration);

      return [
        t('oauth'),
        permissionKey ? t(permissionKey) : '',
        connectedLabel,
      ];
    }

    const webhookLabel = resolveWebhookSetupLabel({
      prSyncMode: integration.prSyncMode,
      pollingIntervalHours,
      hasWebhookSecret: Boolean(integration.webhookSecretEnc),
    });

    return [
      t('token'),
      webhookLabel.key === 'webhookPollingType'
        ? t(webhookLabel.key, { intervalHours: webhookLabel.intervalHours })
        : t(webhookLabel.key),
      connectedLabel,
    ];
  }, [
    connectedLabel,
    infoKind,
    integration,
    pollingIntervalHours,
    repositories,
    t,
  ]);
  const infoLine = infoParts.filter(Boolean).join(' · ');
  const menuHandlers: Record<SourceCodeMenuAction, (() => void) | undefined> = {
    [SOURCE_CODE_MENU_ACTIONS.EDIT_NAME]: onEdit,
    [SOURCE_CODE_MENU_ACTIONS.EDIT_REPOSITORIES]: onAddRepositories,
    [SOURCE_CODE_MENU_ACTIONS.TRANSFORM]: onTransform,
    [SOURCE_CODE_MENU_ACTIONS.DISCONNECT]: onDisconnect,
  };
  const menuItems = resolveSourceCodeMenuActions({
    infoKind,
    canEditName: Boolean(onEdit),
    canEditRepositories: Boolean(onAddRepositories),
    canTransform: Boolean(onTransform),
    canDisconnect: Boolean(onDisconnect),
  }).map(
    (action): MenuItemProps => ({
      value: action,
      label: t(`menu.${MENU_LABEL_KEYS[action]}`),
      onClick: () => menuHandlers[action]?.(),
    }),
  );
  const shouldShowVerification =
    !isZipUpload && typeof isValidIntegration === 'boolean';
  const canAddRepositories =
    Boolean(onAddRepositories) && (!isZipUpload || isMounted);

  return (
    <VStack
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      px={4}
      py={3}
      gap={4}
      alignItems="stretch"
      backgroundColor="bg.neutral.default"
    >
      <HStack>
        {isZipUpload ? (
          <DefaultProviderLogo provider={provider} isZipUpload />
        ) : (
          (providerLogo ?? (
            <DefaultProviderLogo provider={provider} isZipUpload={false} />
          ))
        )}
        <VStack gap={1} alignItems="flex-start" flexGrow={1} overflow="hidden">
          <Box overflow="hidden" w="full">
            <HStack gap={1.5} alignItems="center" mb={1}>
              <Tooltip title={integrationName}>
                <BodyStrong truncate>{integrationName}</BodyStrong>
              </Tooltip>
              {shouldShowVerification ? (
                <Badge
                  size="sm"
                  variant="fill"
                  bg={token(
                    isValidIntegration
                      ? 'colors.bg.neutral.success'
                      : 'colors.bg.neutral.error',
                  )}
                >
                  {isValidIntegration ? t('verified') : t('notVerified')}
                </Badge>
              ) : null}
              {isInternalDeploymentIntegration(integration) ? (
                <Tooltip
                  title={
                    provider
                      ? t('internalDeploymentTooltip', {
                          provider: t(`providers.${provider}`),
                        })
                      : t('internalDeploymentLabel')
                  }
                >
                  <Badge size="sm" variant="outline">
                    {t('internalDeploymentLabel')}
                  </Badge>
                </Tooltip>
              ) : null}
            </HStack>
            <MetaDefault color="fg.neutral.muted">{infoLine}</MetaDefault>
          </Box>
        </VStack>
        {!isReadOnly && menuItems.length > 0 ? (
          <MenuWithItems
            items={menuItems}
            trigger={
              <IconButton
                aria-label={t('menu.moreActions')}
                variant="ghost"
                size="xs"
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                <MoreVerticalIcon size={16} />
              </IconButton>
            }
          />
        ) : null}
      </HStack>
      {isValidIntegration === false && !isZipUpload ? (
        <Alert.Root
          borderColor="border.vulnerability.critical"
          backgroundColor="bg.vulnerability.vulnerable"
          px="4"
          py="2"
        >
          <Alert.Icon asChild>
            <InformationCircleIcon
              size={20}
              color={token('colors.fg.vulnerability.vulnerable')}
            />
          </Alert.Icon>
          <Alert.Content>
            <Alert.Title>{t('notVerifiedAlertTitle')}</Alert.Title>
            <Alert.Description>
              {t('notVerifiedAlertDescription')}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
      {isLoading ? (
        <Skeleton h="50px" w="full" />
      ) : repositories.length > 0 ? (
        <TableGrid
          tableMaxHeight="220px"
          columns={[
            { id: 'repository', title: t('repositoryTable.repository') },
            { id: 'branch', title: t('repositoryTable.branch') },
            {
              id: 'applicationPath',
              title: t('repositoryTable.applicationPath'),
            },
          ]}
          rows={repositories.map((repository) => ({
            id: String(repository.id),
            value: repository.name,
            cells: [
              repository.name,
              repository.targetBranch || '-',
              repository.targetPath || '-',
            ],
          }))}
          placeholder={t('repositoryTable.placeholder')}
        />
      ) : isValidIntegration !== false ? (
        <Alert.Root
          padding={2}
          borderColor="border.neutral.info"
          backgroundColor="bg.neutral.info"
          gap={4}
          alignItems="center"
        >
          <Alert.Icon asChild>
            <InformationCircleIcon
              size={20}
              color={token('colors.fg.neutral.info')}
            />
          </Alert.Icon>
          <Alert.Content>
            <Alert.Description color="fg.neutral.default">
              {t('emptyRepositoriesDescription')}
            </Alert.Description>
          </Alert.Content>
          {canAddRepositories ? (
            <Button
              variant="ghost"
              size="xs"
              fontWeight="medium"
              onClick={onAddRepositories}
            >
              {t('emptyRepositoriesAction')}
            </Button>
          ) : null}
        </Alert.Root>
      ) : null}
    </VStack>
  );
}
